import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import FilterSearch from "../../../components/Filtering/FilterSearch";
import SearchButton from "../../../components/buttons/SearchButton";
import FilterRightBox from "../../../components/Filtering/FilterRightBoxOffering";
import DataTable from "../../../components/DataTable/DataTable";
import CustomButton from "../../../components/buttons/CustomButton";
import { Spinner } from "../../../components/Filtering/Reusable/Spinner";

import { useAuthRoles } from "../../../hooks/useAuthRoles";
import { useFilteredData } from "../../../hooks/useFilteredData";
import { useTableControls } from "../../../hooks/useTableControls";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";

import { getTextByLang } from "../../../utils/formUtils";
import { formatDate, getControlledList } from "../../../utils/constants";

import DistributionForm from "./DistributionForm";
import { usePostDistribution } from "../../../hooks/distributions/usePostDistribution";
import { usePutDistribution } from "../../../hooks/distributions/usePutDistribution";
import { useGetDistributions } from "../../../hooks/distributions/useGetDistribution";
import { useDeleteDistribution } from "../../../hooks/distributions/useDeleteDistribution";
import { useGetContractsPoliciesByIds } from "../../../hooks/policies/useGetContractsPoliciesByIds";
import { useGetVocabsPaginated } from "../../../hooks/vocabularies/useGetVocab";
import AddIcon from "@mui/icons-material/Add";
import { extractIdFromIri } from "../../../utils/extractFromIri";

const EMPTY_POLICIES_BY_ID = Object.freeze({});

function Distribution({
  onSelectionChange,
  selectedIds = [],
  selectionMode = "multiple",
  selectedPolicies,
  selectedPolicyIris,
}) {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { t, i18n } = useTranslation();
  const { isProvider, decoded } = useAuthRoles();
  const {
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    rowCount,
    orgProvider,
    setPagination,
    setRowCount,
    setTableData,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);

  const [openDistDialog, setOpenDistDialog] = useState(false);
  const [selectedDistribution, setSelectedDistribution] = useState(null);

  const [controlledVocabularies, setControlledVocabularies] = useState({
    types: [],
    status: [],
    rights: [],
  });

  const { data: datasetsPage } = useGetVocabsPaginated({
    callApiWithWorker,
    currentLang: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  const participantId = String(decoded?.participant_id || "").trim();

  const vocabOptions = useMemo(() => {
    const content = datasetsPage?.content ?? [];

    return content
      .map((d) => {
        const meta = d?.metadata ?? {};
        const value = d?.access_url || meta?.access_url || d?.identifier;
        if (!value) return null;

        const label =
          getTextByLang(d?.title || meta?.title, i18n.language) ||
          d?.access_url ||
          meta?.access_url ||
          value;

        const version =
          d?.version_info ||
          meta?.version_info ||
          d?.version ||
          meta?.version ||
          "";

        return { label, value, version };
      })
      .filter(Boolean);
  }, [datasetsPage?.content, i18n.language]);

  const selectedRowIds = useMemo(() => {
    if (Array.isArray(selectedIds)) {
      return selectedIds.reduce((acc, id) => {
        if (id != null && String(id).trim() !== "") {
          acc[String(id)] = true;
        }
        return acc;
      }, {});
    }

    if (selectedIds && typeof selectedIds === "object") {
      return Object.keys(selectedIds).reduce((acc, id) => {
        if (selectedIds[id]) acc[String(id)] = true;
        return acc;
      }, {});
    }

    return {};
  }, [selectedIds]);

  const handleSelectedRowsChange = (
    selectedRowsOrSelection,
    maybeRowSelection,
  ) => {
    let rowSelection = maybeRowSelection;
    let selectedRows = [];
    let ids = [];

    if (Array.isArray(selectedRowsOrSelection)) {
      selectedRows = selectedRowsOrSelection;
    } else if (
      selectedRowsOrSelection &&
      typeof selectedRowsOrSelection === "object"
    ) {
      rowSelection = selectedRowsOrSelection;
    }

    if (
      rowSelection &&
      typeof rowSelection === "object" &&
      !Array.isArray(rowSelection)
    ) {
      ids = Object.keys(rowSelection)
        .filter((k) => rowSelection[k])
        .map(String);
    }

    if (ids.length === 0 && selectedRows.length > 0) {
      ids = selectedRows
        .map((r) => r?.original ?? r)
        .map((r) => r?.identifier ?? r?.id)
        .filter(Boolean)
        .map(String);
    } else if (ids.length === 0 && selectedRowsOrSelection) {
      const r = selectedRowsOrSelection?.original ?? selectedRowsOrSelection;
      const id = r?.identifier ?? r?.id;
      ids = id != null ? [String(id)] : [];
    }

    if (selectionMode === "single") ids = ids.slice(0, 1);

    onSelectionChange?.(ids);
  };

  useEffect(() => {
    let mounted = true;

    async function fetchVocabularies() {
      const [types, status, rights] = await Promise.all([
        getControlledList(callApiWithWorker, "filetypes-eu", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "distribution-status", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "access-right-eu", {
          lang: i18n.language,
        }),
      ]);

      if (!mounted) return;

      setControlledVocabularies({ types, status, rights });
    }

    fetchVocabularies();
    return () => {
      mounted = false;
    };
  }, []);

  const {
    data: pageResponse,
    isLoading: isLoadingDistributions,
    isFetching: isFetchingDistributions,
    error,
  } = useGetDistributions({
    callApiWithWorker,
    language: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  const distributionPolicyRefs = useMemo(() => {
    const content = pageResponse?.content ?? [];

    return [...new Set(
      content.flatMap((item) =>
        (Array.isArray(item?.has_policies) ? item.has_policies : []).filter(
          Boolean,
        ),
      ),
    )];
  }, [pageResponse?.content]);

  const {
    data: policiesById = EMPTY_POLICIES_BY_ID,
    isLoading: isLoadingPolicies,
    isFetching: isFetchingPolicies,
  } = useGetContractsPoliciesByIds({
    callApiWithWorker,
    ids: distributionPolicyRefs,
    enabled: distributionPolicyRefs.length > 0,
  });

  const rows = useMemo(() => {
    const content = pageResponse?.content ?? [];
    return content.map((item) => {
      const policyIds = (Array.isArray(item?.has_policies) ? item.has_policies : [])
        .map((policyIri) => extractIdFromIri(policyIri))
        .filter(Boolean);

      const policyAssigners = policyIds
        .map((policyId) => policiesById?.[policyId]?.assigner)
        .filter(Boolean);

      const matchingAssigner = policyAssigners.find((assigner) => {
        const assignerId = extractIdFromIri(assigner) || assigner;
        return String(assignerId || "").trim() === participantId;
      });

      return {
        ...item,
        assigner: matchingAssigner || policyAssigners[0] || item.assigner,
        titleText: getTextByLang(item.title, i18n.language),
        descriptionText: getTextByLang(item.description, i18n.language),
        policiesCount: Array.isArray(item.has_policies)
          ? item.has_policies.length
          : 0,
      };
    });
  }, [pageResponse, policiesById, i18n.language, participantId]);

  useEffect(() => {
    setRowCount(pageResponse?.totalElements ?? 0);
  }, [pageResponse?.totalElements, setRowCount]);

  useEffect(() => {
    setTableData(rows);
  }, [rows, setTableData]);

  const { mutateAsync: createDistribution, isLoading: isCreating } =
    usePostDistribution({ callApiWithWorker, t });

  const { mutateAsync: updateDistribution, isLoading: isUpdating } =
    usePutDistribution({ callApiWithWorker, t });
  const { mutateAsync: deleteDistribution, isPending: isDeleting } =
    useDeleteDistribution({ callApiWithWorker, t });

  const loading =
    isLoadingDistributions ||
    isFetchingDistributions ||
    isLoadingPolicies ||
    isFetchingPolicies ||
    isCreating ||
    isUpdating ||
    isDeleting;

  const handleSubmitDistribution = async (
    payload,
    { mode, initialDistribution } = {},
  ) => {
    if (mode === "edit") {
      await updateDistribution({
        id: extractIdFromIri(initialDistribution.id),
        payload,
      });
    } else {
      await createDistribution(payload);
    }
    setOpenDistDialog(false);
    setSelectedDistribution(null);
  };

  const handleOpenCreateDist = () => {
    setSelectedDistribution(null);
    setOpenDistDialog(true);
  };

  const handleOpenEditDist = (row) => {
    setSelectedDistribution(row.original ?? row);
    setOpenDistDialog(true);
  };

  const handleDeleteDist = async (row) => {
    const record = row?.original ?? row;
    const id = extractIdFromIri(record?.id) || record?.identifier || record?.id;
    if (!id) return;

    const r = await Swal.fire({
      icon: "warning",
      title: t("dataOfferings.deleteMessage") || "Delete?",
      text:
        t("dataOfferings.deleteSubMessage") || "This action is irreversible",
      showCancelButton: true,
      confirmButtonText: t("common.delete") || "Delete",
      cancelButtonText: t("common.cancel") || "Cancel",
    });

    if (!r.isConfirmed) return;

    try {
      await deleteDistribution({ id });
    } catch (e) {
      void e;
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t("wizard.steps.dataset.distribution.updateDate"),
        accessorKey: "updated_at",
        meta: { width: "10%" },
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        header: t("dataOfferings.offerings.dict.name"),
        accessorKey: "titleText",
        meta: { width: "25%" },
        cell: ({ getValue }) => (
          <div className="centered-cell">
            <span className="bold">{getValue()}</span>
          </div>
        ),
      },
      {
        header: t("dataOfferings.offerings.dict.description"),
        accessorKey: "descriptionText",
        meta: { width: "25%" },
      },
      {
        header: t("dataOfferings.offerings.dict.typeOfDistribution"),
        accessorKey: "format",
        meta: { width: "10%" },
        cell: ({ getValue }) => {
          const iri = getValue();
          return extractIdFromIri(iri);
        },
      },
      {
        header: t("wizard.steps.dataset.distribution.policies"),
        accessorKey: "policiesCount",
        meta: { width: "10%" },
      },
    ],
    [t],
  );

  const filteredData = useFilteredData(rows, searchTerm, filters, columns);

  useEffect(() => {
    if (error) {
      console.error("Error fetching distributions:", error);
    }
  }, [error]);

  return (
    <section>
      <div>
        <div className="W100 labeltittle">
          <p className="pt1">
            {t("wizard.steps.dataset.distribution.subtitle")}
          </p>

          {isProvider && (
            <CustomButton
              onClick={handleOpenCreateDist}
              disabled={loading}
              startIcon={<AddIcon />}
            >
              {t("wizard.steps.dataset.distribution.createDistribution")}
            </CustomButton>
          )}
        </div>

        <div className="W100 plr2 filteractions pt3">
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>

        <FilterRightBox
          open={open}
          onClose={handleClose}
          onApplyFilters={handleApplyFilters}
        />

        <div className="W100 bgwhite mtb2">
          <div className="plr3 ptb">
            <DataTable
              data={filteredData}
              columns={columns}
              onEdit={handleOpenEditDist}
              onDelete={handleDeleteDist}
              showActions={orgProvider}
              initialSorting={[{ id: "updated_at", desc: true }]}
              enableRowSelection
              selectionMode={selectionMode}
              getRowId={(row) => row.identifier ?? row.id}
              selectedRowIds={selectedRowIds}
              onSelectedRowsChange={handleSelectedRowsChange}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
            />
          </div>
        </div>

        <DistributionForm
          open={openDistDialog}
          onClose={() => {
            setOpenDistDialog(false);
            setSelectedDistribution(null);
          }}
          t={t}
          i18n={i18n}
          onSubmit={handleSubmitDistribution}
          loading={loading}
          formatOptions={controlledVocabularies.types}
          statusOptions={controlledVocabularies.status}
          rightsOptions={controlledVocabularies.rights}
          initialDistribution={selectedDistribution}
          selectedPolicies={selectedPolicies}
          selectedPolicyIris={selectedPolicyIris}
          vocabOptions={vocabOptions}
        />
      </div>

      {loading && (
        <div className="loading">
          <Spinner size="large" color="var(--main-color)" />
        </div>
      )}
    </section>
  );
}

export default Distribution;
